import { useQuery } from '@tanstack/react-query';
import { fetchConfig } from '@/api/config';

export function useConfig() {
  return useQuery({
    queryKey: ['config'],
    queryFn: ({ signal }) => fetchConfig(signal),
    staleTime: Infinity,
    retry: 1
  });
}

/**
 * The short environment label shown next to the brand — `'dev'` locally,
 * `'alpha'` on staging — so a staging tab is never mistaken for production.
 * `null` in production, and also before the config request has resolved,
 * which means alpha shows the tag once the request lands rather than
 * flashing anything wrong in the meantime.
 */
export function useEnvironmentTag(): string | null {
  const config = useConfig();
  if (import.meta.env.DEV) return 'dev';
  const env = config.data?.environment;
  if (typeof env === 'string' && env !== '' && env !== 'production') return env;
  return null;
}

export interface FeatureFlags {
  /**
   * Screens that exist only on a developer's machine: the vite dev server, or
   * a worker whose ENVIRONMENT is 'development'. Alpha and production share
   * one build and both answer false, so gating on this is how a feature ships
   * in the bundle without appearing anywhere a customer can reach.
   */
  devOnly: boolean;
}

export function useFeatureFlags(): FeatureFlags {
  const config = useConfig();
  return {
    devOnly: import.meta.env.DEV || config.data?.environment === 'development'
  };
}
